"use client";


import { getBrowserClient } from "@/utils/supabase/client";
import { phCapture, phGetDistinctId } from "@/utils/analytics";
import { getAttributionForEvent } from "@/utils/attribution";
import { sanitizePricingExperimentMetadata } from "@/lib/pricing-experiment";
import { loadStripeWithRetry } from "@/lib/stripe-js-loader";

export const getStripe = () =>
  loadStripeWithRetry(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY);

export const REWARDFUL_READY_TIMEOUT_MS = 3000;

/**
 * @returns {Promise<string|null>}
 */
export const getRewardfulReferral = async () => {
  if (typeof window === "undefined") {
    return null;
  }

  const immediateReferral = window?.Rewardful?.referral;
  if (immediateReferral) {
    phCapture("rewardful_referral_captured", { source: "immediate", pollAttempts: 0 });
    return immediateReferral;
  }

  if (!window.Rewardful) {
    phCapture("rewardful_referral_missing", { source: "script_not_loaded" });
    return null;
  }

  const rewardfulFn = window?.rewardful;
  if (typeof rewardfulFn !== "function") {
    phCapture("rewardful_referral_missing", { source: "queue_unavailable" });
    return null;
  }

  return new Promise((resolve) => {
    let settled = false;
    let pollAttempts = 0;
    const maxPollAttempts = REWARDFUL_READY_TIMEOUT_MS / 100;

    const finish = (referral, source) => {
      if (settled) return;
      settled = true;

      if (referral) {
        phCapture("rewardful_referral_captured", {
          source,
          pollAttempts,
          timing: pollAttempts * 100
        });
      } else {
        phCapture("rewardful_referral_missing", {
          source,
          pollAttempts
        });
      }
      resolve(referral);
    };

    const timeoutId = setTimeout(() => finish(null, "timeout"), REWARDFUL_READY_TIMEOUT_MS);

    try {
      rewardfulFn("ready", () => {
        const pollForReferral = () => {
          pollAttempts++;
          const referral = window?.Rewardful?.referral;

          if (referral) {
            clearTimeout(timeoutId);
            finish(referral, "tracked");
          } else if (pollAttempts < maxPollAttempts) {
            setTimeout(pollForReferral, 100);
          } else {
            clearTimeout(timeoutId);
            finish(null, "poll_exhausted");
          }
        };

        pollForReferral();
      });
    } catch (error) {
      clearTimeout(timeoutId);
      finish(null, "error");
    }
  });
};


const REWARDFUL_REFERRAL_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Enhanced checkout options interface
 * @typedef {Object} CheckoutOptions
 * @property {string} priceId - The Stripe price ID for the subscription
 * @property {string} subscriptionType - The type of subscription (monthly/yearly)
 * @property {string} [couponCode] - Optional coupon code to apply (legacy)
 * @property {"promotion_code"|"coupon"} [discountType] - Discount type for pre-validated promo
 * @property {string} [discountId] - Promotion code or coupon ID to apply
 * @property {string} [ui_mode] - Checkout UI mode (hosted/embedded)
 * @property {boolean} [allow_promotion_codes] - Allow users to enter promotion codes
 * @property {Array<string>} [payment_method_types] - Allowed payment method types
 * @property {string} [referralSource] - Source of the referral
 * @property {string} [redirectTo] - Optional redirect target after checkout
 * @property {string} [pricing_experiment_id] - Pricing experiment identifier
 * @property {string} [pricing_experiment_variant] - Pricing experiment variant key
 * @property {string} [pricing_experiment_price_id] - Experiment-assigned Stripe price ID
 * @property {function} [onError] - Error callback function
 */

/**
 * Initiate checkout for subscription with enhanced options
 * @param {CheckoutOptions} options - Checkout options
 * @returns {Promise<{ sessionId: string, url: string } | { error: string, redirectUrl?: string }>}
 */
export const initiateCheckout = async (options) => {
  try {
    const supabase = getBrowserClient();

    const { data: { session } } = await supabase.auth.getSession();

    if (!session || !session.access_token) {
      throw new Error("No active session found. Please log in again.");
    }

    const derivedSurface =
      options.surface ||
      (typeof window !== "undefined"
        ? (window.location.pathname.startsWith("/pricing")
            ? "pricing_page"
            : "home_pricing_section")
        : undefined);

    const attribution = getAttributionForEvent();
    const phDistinctId = phGetDistinctId();
    const pricingExperiment = sanitizePricingExperimentMetadata({
      pricing_experiment_id: options.pricing_experiment_id,
      pricing_experiment_variant: options.pricing_experiment_variant,
      pricing_experiment_price_id: options.pricing_experiment_price_id,
    });

    const requestBody = {
      priceId: options.priceId,
      attribution,
      ph_distinct_id: phDistinctId,
      surface: derivedSurface,
      ...pricingExperiment,
    };

    if (options.redirectTo) {
      requestBody.redirectTo = options.redirectTo;
    }

    const rewardfulReferral = await getRewardfulReferral();
    if (rewardfulReferral && REWARDFUL_REFERRAL_REGEX.test(rewardfulReferral)) {
      requestBody.rewardfulReferral = rewardfulReferral;
    }

    if (options.discountType && options.discountId) {
      requestBody.discountType = options.discountType;
      requestBody.discountId = options.discountId;
    } else if (options.couponCode) {
      requestBody.couponCode = options.couponCode;
    }

    const response = await fetch("/api/create-checkout-session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(requestBody),
      credentials: "include",
    });

    const checkoutSession = await response.json();

    if (!response.ok) {
      throw Object.assign(
        new Error(checkoutSession.error || checkoutSession.details || "Failed to create checkout session"),
        { purchaseRoute: checkoutSession.purchaseRoute },
      );
    }

    if (checkoutSession.url) {
      window.location.href = checkoutSession.url;
      return checkoutSession;
    }

    if (checkoutSession.sessionId) {
      const stripe = await getStripe();
      const { error } = await stripe.redirectToCheckout({
        sessionId: checkoutSession.sessionId,
      });

      if (error) {
        throw new Error(error.message);
      }
    }

    return checkoutSession;
  } catch (error) {
    console.error("Error in initiateCheckout:", error);

    if (typeof options.onError === "function") {
      options.onError(error);
    }

    return {
      error: error.message || "An unknown error occurred",
      purchaseRoute: error.purchaseRoute,
    };
  }
};

/**
 * Redirect to Stripe customer portal for subscription management
 * @param {Object} options - Optional parameters for portal redirection
 * @returns {Promise<{ url: string } | { error: string }>}
 */
export const redirectToCustomerPortal = async (options = {}) => {
  try {
    const supabase = getBrowserClient();
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session || !session.access_token) {
      throw new Error("No active session found. Please log in again.");
    }

    const response = await fetch("/api/create-portal-session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(options),
      credentials: "include",
      cache: "no-store",
    });

    const portalSession = await response.json();

    if (!response.ok) {
      console.error("Portal session error details:", portalSession);
      throw new Error(
        portalSession.error ||
          portalSession.details ||
          "Failed to create portal session"
      );
    }

    if (portalSession.url) {
      window.location.assign(portalSession.url);
    }

    return portalSession;
  } catch (error) {
    console.error("Error in redirectToCustomerPortal:", error);

    if (options.onError && typeof options.onError === "function") {
      options.onError(error);
    }

    throw error;
  }
};

/**
 * Guest checkout options interface
 * @typedef {Object} GuestCheckoutOptions
 * @property {string} [priceId] - The Stripe price ID
 * @property {string} [discountType] - Type of discount: "promotion_code" or "coupon"
 * @property {string} [discountId] - ID of the promotion code or coupon to apply
 * @property {string} [email] - Optional email for guest checkout
 * @property {string} [surface] - Where the checkout was initiated from
 * @property {string} [redirectTo] - Optional redirect target after checkout
 * @property {string} [pricing_experiment_id] - Pricing experiment identifier
 * @property {string} [pricing_experiment_variant] - Pricing experiment variant key
 * @property {string} [pricing_experiment_price_id] - Experiment-assigned Stripe price ID
 * @property {function} [onError] - Error callback function
 */
export const initiateGuestCheckout = async ({
  priceId,
  discountType,
  discountId,
  email,
  onError,
  surface,
  redirectTo,
  pricing_experiment_id,
  pricing_experiment_variant,
  pricing_experiment_price_id,
} = {}) => {
  try {
    const derivedSurface =
      surface ||
      (typeof window !== "undefined"
        ? (window.location.pathname.startsWith("/pricing")
            ? "pricing_page"
            : "home_pricing_section")
        : undefined);

    const attribution = getAttributionForEvent();
    const phDistinctId = phGetDistinctId();
    const pricingExperiment = sanitizePricingExperimentMetadata({
      pricing_experiment_id,
      pricing_experiment_variant,
      pricing_experiment_price_id,
    });

    const requestBody = {
      priceId,
      attribution,
      ph_distinct_id: phDistinctId,
      surface: derivedSurface,
      ...pricingExperiment,
    };

    if (discountType && discountId) {
      requestBody.discountType = discountType;
      requestBody.discountId = discountId;
    }

    if (email) {
      requestBody.email = email;
    }

    if (redirectTo) {
      requestBody.redirectTo = redirectTo;
    }

    const rewardfulReferral = await getRewardfulReferral();
    if (rewardfulReferral && REWARDFUL_REFERRAL_REGEX.test(rewardfulReferral)) {
      requestBody.rewardfulReferral = rewardfulReferral;
    }

    const response = await fetch("/api/create-guest-checkout-session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
      credentials: "include",
    });

    const checkoutSession = await response.json();

    if (!response.ok) {
      throw new Error(
        checkoutSession.details ||
          checkoutSession.error ||
          "Failed to create checkout session"
      );
    }

    if (checkoutSession.url) {
      window.location.href = checkoutSession.url;
      return checkoutSession;
    }

    if (checkoutSession.sessionId) {
      const stripe = await getStripe();
      const { error } = await stripe.redirectToCheckout({
        sessionId: checkoutSession.sessionId,
      });

      if (error) {
        throw new Error(error.message);
      }
    }

    return checkoutSession;
  } catch (error) {
    console.error("Error in initiateGuestCheckout:", error);

    if (typeof onError === "function") {
      onError(error);
    }

    return {
      error: error.message || "An unknown error occurred",
    };
  }
};

const requestSetupSession = async ({
  priceId,
  email,
  discountType,
  discountId,
  surface,
  redirectTo,
  wallet,
  pricing_experiment_id,
  pricing_experiment_variant,
  pricing_experiment_price_id,
}) => {
  const supabase = getBrowserClient();
  const { data: { session } } = await supabase.auth.getSession();

  const derivedSurface =
    surface ||
    (typeof window !== "undefined"
      ? (window.location.pathname.startsWith("/pricing")
          ? "pricing_page"
          : "home_pricing_section")
      : undefined);

  const attribution = getAttributionForEvent();
  const phDistinctId = phGetDistinctId();
  const pricingExperiment = sanitizePricingExperimentMetadata({
    pricing_experiment_id,
    pricing_experiment_variant,
    pricing_experiment_price_id,
  });

  const requestBody = {
    priceId,
    ...(email ? { email } : {}),
    ...(wallet ? { wallet: true } : {}),
    attribution,
    ph_distinct_id: phDistinctId,
    surface: derivedSurface,
    ...pricingExperiment,
  };

  if (discountType && discountId) {
    requestBody.discountType = discountType;
    requestBody.discountId = discountId;
  }

  if (redirectTo) {
    requestBody.redirectTo = redirectTo;
  }

  const rewardfulReferral = await getRewardfulReferral();
  if (rewardfulReferral && REWARDFUL_REFERRAL_REGEX.test(rewardfulReferral)) {
    requestBody.rewardfulReferral = rewardfulReferral;
  }

  const response = await fetch("/api/create-setup-session", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify(requestBody),
    credentials: "include",
  });

  const result = await response.json();

  if (!response.ok) {
    throw Object.assign(
      new Error(result.details || result.error || "Failed to create setup session"),
      { purchaseRoute: result.purchaseRoute },
    );
  }

  return result;
};

/**
 * Guest setup options interface
 * @typedef {Object} GuestSetupOptions
 * @property {string} priceId - The Stripe price ID (must be trial-eligible)
 * @property {string} email - User email (required for setup flow)
 * @property {string} [discountType] - Type of discount: "promotion_code" or "coupon"
 * @property {string} [discountId] - ID of the promotion code or coupon to apply
 * @property {string} [surface] - Where the checkout was initiated from
 * @property {string} [redirectTo] - Optional redirect target after checkout
 * @property {string} [pricing_experiment_id] - Pricing experiment identifier
 * @property {string} [pricing_experiment_variant] - Pricing experiment variant key
 * @property {string} [pricing_experiment_price_id] - Experiment-assigned Stripe price ID
 * @property {function} [onError] - Error callback function
 */

/**
 * Initiate setup-mode checkout for trial-eligible plans (monthly with trial).
 * This flow collects card details first, then verifies funds with $1 auth hold.
 * @param {GuestSetupOptions} options - Setup options
 * @returns {Promise<{ url: string } | { error: string }>}
 */
export const initiateGuestSetup = async ({
  priceId,
  email,
  discountType,
  discountId,
  onError,
  surface,
  redirectTo,
  pricing_experiment_id,
  pricing_experiment_variant,
  pricing_experiment_price_id,
} = {}) => {
  try {
    if (!email) {
      throw new Error("Email is required for card verification");
    }

    const result = await requestSetupSession({
      priceId,
      email,
      discountType,
      discountId,
      surface,
      redirectTo,
      pricing_experiment_id,
      pricing_experiment_variant,
      pricing_experiment_price_id,
    });

    if (result.url) {
      window.location.href = result.url;
      return result;
    }

    throw new Error("No redirect URL returned from setup session");
  } catch (error) {
    console.error("Error in initiateGuestSetup:", error);

    if (typeof onError === "function") {
      onError(error);
    }

    return {
      error: error.message || "An unknown error occurred",
      purchaseRoute: error.purchaseRoute,
    };
  }
};

export const startWalletSetup = async ({
  priceId,
  redirectTo,
  pricing_experiment_id,
  pricing_experiment_variant,
  pricing_experiment_price_id,
}) => {
  const result = await requestSetupSession({
    priceId,
    surface: "checkout_wallet",
    redirectTo,
    wallet: true,
    pricing_experiment_id,
    pricing_experiment_variant,
    pricing_experiment_price_id,
  });

  if (!result.clientSecret) {
    throw new Error("No client secret returned from setup session");
  }

  return result.clientSecret;
};
